// Follow-ups — the staff call list (docs/follow-ups-plan.md §1.6, §2, §3).
//
// Leases a resident has not signed and rental references a previous landlord
// has not answered, 24 hours after they were sent. The server derives the list
// (nothing here sorts or filters it) and every mutation answers with the whole
// refreshed list, so the page never issues a second GET after a snooze or a
// done mark.
//
// The wire types below are a VERBATIM copy of the API's
// `apps/api/src/lib/followUps/types.ts` (the W1 contract). Change them only in
// lockstep with that file and with the digest that renders from it.
//
// Never an id in a text field: ids travel only in `key` and the AppFolio
// `appfolioUrl` href.

import { api } from './api';

// ---------------------------------------------------------------------------
// Wire types (copied verbatim from apps/api/src/lib/followUps/types.ts)
// ---------------------------------------------------------------------------

export type FollowUpKind = 'lease' | 'rv' | 'verifier_call';
/** 'called' — someone made the call (back in 36 h if still waiting); 'not_needed' — no call for this send. */
export type FollowUpDoneReason = 'called' | 'not_needed';
export interface FollowUpPerson { name: string; email: string | null; phone: string | null; }
export interface FollowUpItem {
  key: string;                                  // lease#<groupId> | rv#<groupId>#<sk> | verifier_call#<caseId>#<verificationId>
  kind: FollowUpKind;
  primary: FollowUpPerson & { appfolioUrl: string | null; appfolioTarget: 'application' | 'unit' | null };
  unit: { name: string | null; address: string | null };
  property: { name: string | null; address: string | null };
  waitingSince: string;                         // ISO
  waitingHours: number;
  lease?: { isRenewal: boolean; outstanding: { name: string; role: string }[]; signedCount: number; totalCount: number; lastReminderAt: string | null };
  rv?: { landlord: FollowUpPerson; priorAddress: string | null; applicantNames: string[]; openedAt: string | null; reminderCount: number; lastReminderAt: string | null; linkExpiresAt: string | null;
    /** WEL-181: where the household stands in AppFolio — 'approved' (an application approved, not yet a tenant) or 'tenant' (converted to a tenancy); null otherwise. Drives the row's badge only: the reference keeps reminding until staff cancel it. */
    applicationStage: 'approved' | 'tenant' | null };
  // WEL-181: a reference row's Cancel posts `POST /api/requests/cancel` with the id
  // `rental_verification:<groupId>:<sk>`, derived from `key` (`rv#<groupId>#<sk>`) — no id
  // travels in any other field (the noIds rule).
  /**
   * RRA v2.1 (W3.14, Miles's Decision 5): a scheduled front-line verifier call is due.
   * The row carries the verifier's name and phone (in `primary`) and "for {subjectName}",
   * and nothing else about the request. `callSheetUrl` is the signed-in call sheet link;
   * it is the row's only action (the call report there clears the row — no snooze, no done mark).
   */
  verifierCall?: { subjectName: string; callNumber: number; callSheetUrl: string };
  snooze: { until: string; by: string; at: string } | null;
  /** Set on items in `done`: why, who, when, and when it returns (null: not while this send stands). */
  done: { reason: FollowUpDoneReason; by: string; at: string; until: string | null } | null;
}
export interface FollowUpsResponse {
  generatedAt: string;
  due: FollowUpItem[];
  snoozed: FollowUpItem[];
  /** Items marked done on their current send, most recently marked first. */
  done: FollowUpItem[];
  /** Families whose read failed ('signatures', 'rental_verifications', 'renewal_drafts', 'active_apps', 'snoozes', 'verifier_calls'): the list may be incomplete. Empty when complete. */
  degraded: string[];
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** The only snooze lengths the server accepts (anything else is a 400). */
export const SNOOZE_DAYS = [1, 3, 7] as const;
export type SnoozeDays = (typeof SNOOZE_DAYS)[number];

/** A key carries `#` (and an rv key a normalized address), so it is always
 *  encoded as ONE path segment. */
function snoozePath(key: string): string {
  return `/api/follow-ups/${encodeURIComponent(key)}/snooze`;
}

/** GET /api/follow-ups — the whole list: due, snoozed and done. Never writes. */
export function fetchFollowUps(): Promise<FollowUpsResponse> {
  return api.get<FollowUpsResponse>('/api/follow-ups');
}

/** Hide an item for everyone until `days` from now; answers with the refreshed list. */
export function snoozeFollowUp(key: string, days: SnoozeDays): Promise<FollowUpsResponse> {
  return api.post<FollowUpsResponse>(snoozePath(key), { days });
}

/** Put a snoozed item back on the list; answers with the refreshed list. */
export function unsnoozeFollowUp(key: string): Promise<FollowUpsResponse> {
  return api.del<FollowUpsResponse>(snoozePath(key));
}

function donePath(key: string): string {
  return `/api/follow-ups/${encodeURIComponent(key)}/done`;
}

/**
 * Take an item off the list for everyone: 'called' for 36 hours (back after
 * that if it is still waiting), 'not_needed' until the lease is revised or the
 * reference re-sent. Answers with the refreshed list.
 */
export function markFollowUpDone(key: string, reason: FollowUpDoneReason): Promise<FollowUpsResponse> {
  return api.post<FollowUpsResponse>(donePath(key), { reason });
}

/** Undo a done mark: the item is due again. Answers with the refreshed list. */
export function putBackFollowUp(key: string): Promise<FollowUpsResponse> {
  return api.del<FollowUpsResponse>(donePath(key));
}
